import { defineStore } from 'pinia';
import { Unknown } from 'runtypes';
import { ShareTheTheDocumentQueryCallback } from 'src/stores/document/callbacks';
import { IDO_PRINT_LIST_VIEW_MODE_LARGE } from 'src/stores/idoPrintList/localStorageKeys';
import { GetTheFilledIdoPrintDocumentQuery, GetTheIdoPrintListQuery } from 'src/stores/idoPrintList/queries';
import createIdoPrintListState, { IdoPrintListEntry } from 'src/stores/idoPrintList/state';

import { DocumentContentRunType } from 'src/models/document';
import { GenericActivityListPagination } from 'src/models/genericActivityList';
import { IdoPrintListItem } from 'src/models/idoPrint';

import { GetTheFilledIdoPrintDocumentQueryCallback, GetTheIdoPrintListQueryCallback } from './callbacks';
import {
	ApplyThePrintListPayload,
	FetchThePrintListPayload,
	PrintTheDocumentPayload,
	SetPrintPaginationPagePayload,
	SetPrintSearchTermPayload,
	SetTheStatusForPrintAggregateIdsPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;

const createDefaultPagination = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

export const idoPrintListKey = (instanceId: number, idoId: number): string => `${instanceId}#${idoId}`;

export const useIdoPrintListStore = defineStore('idoPrintList', {
	actions: {
		applyThePrintList(payload: ApplyThePrintListPayload): void {
			const key = idoPrintListKey(payload.instanceId, payload.idoId);

			this.printListsByKey = {
				...this.printListsByKey,
				[key]: {
					loading: false,
					rows: payload.rows,
					totalCount: payload.totalCount,
				},
			};
		},
		async fetchThePrintList(payload: FetchThePrintListPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				return;
			}

			const key = idoPrintListKey(payload.instanceId, payload.idoId);
			const existingEntry = this.printListsByKey[key];

			this.printListsByKey = {
				...this.printListsByKey,
				[key]: {
					loading: true,
					rows: existingEntry?.rows ?? [],
					totalCount: existingEntry?.totalCount ?? 0,
				},
			};

			await payload.queryStore.query<Array<unknown>>({
				callback: new GetTheIdoPrintListQueryCallback(this, payload.idoId, payload.instanceId),
				createTime: new Date(),
				query: new GetTheIdoPrintListQuery(payload.idoId, payload.instanceId),
				responseType: 'json',
				runType: Unknown,
			});
		},
		finishPrintingDocument(aggregateId: string): void {
			this.printingDocumentIds = this.printingDocumentIds.filter(
				(printingAggregateId) => printingAggregateId !== aggregateId
			);
		},
		printTheDocument(payload: PrintTheDocumentPayload): void {
			const query = new GetTheFilledIdoPrintDocumentQuery(
				payload.documentId,
				payload.instanceId,
				payload.idoId,
				payload.apId
			);

			this.startPrintingDocument(payload.aggregateId);

			const documentCallback = new ShareTheTheDocumentQueryCallback(payload.filename);

			void payload.queryStore.query<Blob>({
				callback: new GetTheFilledIdoPrintDocumentQueryCallback(
					this,
					payload.aggregateId,
					documentCallback,
					payload.t
				),
				createTime: new Date(),
				query,
				responseType: 'blob',
				runType: DocumentContentRunType,
			});
		},
		setListViewModeLarge(value: boolean): void {
			localStorage.setItem(IDO_PRINT_LIST_VIEW_MODE_LARGE, JSON.stringify(value));
			this.listViewModeLarge = value;
		},
		setPrintPaginationPage(payload: SetPrintPaginationPagePayload): void {
			const key = idoPrintListKey(payload.instanceId, payload.idoId);

			this.pagination = {
				...this.pagination,
				[key]: { ...this.printPaginationForKey(key), page: payload.page },
			};
		},
		setPrintSearchTerm(payload: SetPrintSearchTermPayload): void {
			const key = idoPrintListKey(payload.instanceId, payload.idoId);

			this.printSearchTermByKey = {
				...this.printSearchTermByKey,
				[key]: payload.searchTerm,
			};
		},
		setTheStatusForPrintAggregateIds(payload: SetTheStatusForPrintAggregateIdsPayload): void {
			if (payload.aggregateIds.length === 0) {
				return;
			}

			this.printListsByKey = Object.entries(this.printListsByKey).reduce<Record<string, IdoPrintListEntry>>(
				(updatedPrintListsByKey, [key, entry]) => ({
					...updatedPrintListsByKey,
					[key]: {
						...entry,
						rows: entry.rows.map((row) =>
							payload.aggregateIds.includes(row.aggregateId)
								? { ...row, statusButton: { ...row.statusButton, status: payload.status } }
								: row
						),
					},
				}),
				{}
			);
		},
		startPrintingDocument(aggregateId: string): void {
			if (this.printingDocumentIds.includes(aggregateId)) {
				return;
			}

			this.printingDocumentIds = [...this.printingDocumentIds, aggregateId];
		},
	},
	getters: {
		hasLoadedPrintListForKey(state): (instanceId: number, idoId: number) => boolean {
			return (instanceId: number, idoId: number): boolean =>
				state.printListsByKey[idoPrintListKey(instanceId, idoId)]?.loading === false;
		},
		hasPrintTemplates(state): (instanceId: number, idoId: number) => boolean {
			return (instanceId: number, idoId: number): boolean => {
				const entry = state.printListsByKey[idoPrintListKey(instanceId, idoId)];

				return entry !== undefined && entry.totalCount > 0;
			};
		},
		isPrintingDocument(state): (aggregateId: string) => boolean {
			return (aggregateId: string): boolean => state.printingDocumentIds.includes(aggregateId);
		},
		printListForKey(state): (key: string) => Array<IdoPrintListItem> {
			return (key: string): Array<IdoPrintListItem> => state.printListsByKey[key]?.rows ?? [];
		},
		printPaginationForKey(state): (key: string) => GenericActivityListPagination {
			return (key: string): GenericActivityListPagination => state.pagination[key] ?? createDefaultPagination();
		},
		printSearchTermForKey(state): (key: string) => string {
			return (key: string): string => state.printSearchTermByKey[key] ?? '';
		},
	},
	state: createIdoPrintListState,
});

export * from './interfaces';
